import React, { useEffect, useState } from "react";
import { TableBody, TableCell, TableRow, Tooltip } from "@mui/material";
import { locale } from "../../utils/LocaleManager";
import {
  convertTimeString,
  isTextNeedsEllipsis,
  toPercentage,
} from "../../utils/utilFunctions";
import DataTable from "../DataTable";
import { useContentStore } from "../../stores/contentStore";
import TableMenu from "../TableMenu";
import { TagTypeEnum } from "../../enums/TagTypeEnum";
import {
  antiFlashWhite,
  black,
  diamond,
  limeGreen,
  paleRed,
  paleViolet,
  paleYellow,
} from "../../utils/StyleHelpers";
import Circle from "../Circle";

const TableDetails = (props: any) => {
  const [selectedRow, setSelectedRow] = useState<any>(
    props?.selectedRow || null,
  );
  const setSelectedCurrentTime = useContentStore(
    (state: any) => state.setSelectedCurrentTime,
  );
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const tagDetails = useContentStore((state: any) => state.tagDetails);
  const setTagDetails = useContentStore((state: any) => state.setTagDetails);
  const [contextMenu, setContextMenu] = React.useState<{
    mouseX: number;
    mouseY: number;
  } | null>(null);
  const [open, setOpen] = useState(false);
  const setIsModify = useContentStore((state: any) => state.setIsModify);
  const setIsSelected = useContentStore((state: any) => state.setIsSelected);

  const onSelected = (e: React.MouseEvent<HTMLElement>, selected: any) => {
    e.preventDefault();
    setSelectedRow(selected);
    setSelectedCurrentTime(selected.start);
    setIsModify(true);
    if (selected?.type === TagTypeEnum.transcript) {
      setIsSelected(true);
    } else {
      setIsSelected(false);
    }
  };

  const renderNumber = (index: number) => {
    return index + 1;
  };

  const TableHeader = (text: string) => {
    return (
      <TableCell
        style={{
          color: black,
          textOverflow: "ellipsis",
          fontSize: "1rem",
          fontWeight: 600,
          lineHeight: "normal",
          backgroundColor: antiFlashWhite,
        }}
        align="left"
      >
        {text}
      </TableCell>
    );
  };

  const colorsCodes = (tagType: any) => {
    switch (tagType) {
      case TagTypeEnum.OCR:
        return limeGreen;
      case TagTypeEnum.label:
        return paleYellow;
      case TagTypeEnum.object:
        return paleRed;
      case TagTypeEnum.transcript:
        return paleViolet;
    }
  };

  const onContextMenuOpen = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    setAnchorEl(null);
    setContextMenu({
      mouseX: e.clientX + 2,
      mouseY: e.clientY - 6,
    });
    setOpen(true);
  };

  const onMarkDeletion = () => {
    const tempDetails = tagDetails.slice(0);

    if (!selectedRow?.tagId) {
      setTagDetails(tempDetails.filter((item: any) => item !== selectedRow));
      props.setTag("");
      props.setType("Label");
      setIsModify(false);
      return;
    }

    const index = tempDetails.findIndex((item: any) => item === selectedRow);
    tempDetails[index] = { ...selectedRow, deleted: true };
    setTagDetails(tempDetails);
    props.setTag("");
    props.setType("Label");
    setIsModify(false);
  };

  const onUnMarkDeletion = () => {
    const tempDetails = tagDetails.map((item: any) => ({
      ...item,
      deleted: false,
    }));
    setTagDetails(tempDetails);
    setIsModify(false);
  };

  useEffect(() => {
    setSelectedRow(null);
  }, [tagDetails]);

  useEffect(() => {
  }, [props.selectedFilter]);

  return (
    <>
      <DataTable
        minWidth={535}
        label="details table"
        id="st-tabledetails"
        tableHeader={
          <>
            <TableCell style={{ backgroundColor: antiFlashWhite }}>
              {locale.hashtag}
            </TableCell>
            {TableHeader(locale.timestamp)}
            {TableHeader(locale.type)}
            {TableHeader(locale.tag)}
            {TableHeader(locale.confidence)}
          </>
        }
        tableBody={
          <TableBody>
            {props.tagDetails
              ?.filter((item: any) => {
                return props.selectedFilter.length
                  ? props.selectedFilter.includes(item.type)
                  : true;
              })
              ?.filter((item: any) => {
                return props.searchKeyword
                  ? item.name
                      ?.toLowerCase()
                      .includes(props.searchKeyword.toLowerCase())
                  : item.name?.toLowerCase();
              }).length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  {locale.no_results}
                </TableCell>
              </TableRow>
            )}
            {props.tagDetails
              ?.filter((item: any) => {
                return props.selectedFilter.length
                  ? props.selectedFilter.includes(item.type)
                  : true;
              })
              ?.filter((item: any) => {
                return props.searchKeyword
                  ? item.name
                      ?.toLowerCase()
                      .includes(props.searchKeyword.toLowerCase())
                  : item.name?.toLowerCase();
              })
              .map((item: any, index: any) => (
                <TableRow
                  id={`st-row-${convertTimeString(item.start)}`}
                  sx={{
                    "&:last-child td, &:last-child th": { border: 0 },
                    cursor: "pointer",
                  }}
                  key={index}
                  onClick={(e) => {
                    props.onSelectedRow(item);
                    onSelected(e, item);
                  }}
                  style={{
                    background: selectedRow === item ? diamond : "",
                  }}
                  hover={true}
                  onContextMenu={(e) => {
                    onSelected(e, item);
                    onContextMenuOpen(e);
                  }}
                >
                  <TableCell component="th" scope="row">
                    {renderNumber(index)}
                  </TableCell>
                  <TableCell align="left">
                    {convertTimeString(item.start)}
                  </TableCell>
                  <TableCell align="left">
                    <Circle
                      toolTipTitle={item.type}
                      color={colorsCodes(item.type)}
                    />
                  </TableCell>
                  <TableCell align="left">
                    <Tooltip
                      title={isTextNeedsEllipsis(item?.name) ? item?.name : ""}
                      placement="top-end"
                      arrow
                    >
                      {isTextNeedsEllipsis(item?.name)
                        ? `${item.name.substring(0, 25)}...`
                        : item.name}
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">
                    {toPercentage(item.confidence)}%
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        }
      />
      <TableMenu
        anchorEl={anchorEl}
        contextMenu={contextMenu}
        open={open}
        setOpen={setOpen}
        selectedRow={selectedRow}
        setSelectedRow={setSelectedRow}
        onMarkDeletion={onMarkDeletion}
        onUnMarkDeletion={onUnMarkDeletion}
      />
    </>
  );
};

export default TableDetails;
